import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Differentiation: Composite, Implicit, and Inverse Functions",
  examWeight: "9–13%",
  focus: "Chain Rule, Implicit Differentiation, Inverse Functions",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: The Chain Rule",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Concept & Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Chain Rule is the tool used to differentiate <strong>composite functions</strong>, where one function is nested inside another (e.g., <LatexText text="$y = f(g(x))$" />).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. The Rule</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$y = f(u)$" /> is a differentiable function of <LatexText text="$u$" />, and <LatexText text="$u = g(x)$" /> is a differentiable function of <LatexText text="$x$" />, then:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">Chain Rule (Lagrange Notation)</h4>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$\frac{d}{dx}[f(g(x))] = f'(g(x)) \cdot g'(x)$$" />
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">Chain Rule (Leibniz Notation)</h4>
              <div className="mt-3 bg-white p-3 rounded border border-green-200">
                <LatexText text="$$\frac{dy}{dx} = \frac{dy}{du} \cdot \frac{du}{dx}$$" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. The "Outside-Inside" Strategy (Peeling the Onion)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To apply the Chain Rule correctly, you must identify the "inner" and "outer" layers:
            </p>
            <div className="space-y-2 text-slate-700 mb-4">
              <p><strong>1.</strong> Differentiate the <strong>outside</strong> function, keeping the <strong>inside</strong> function exactly the same.</p>
              <p><strong>2.</strong> Multiply by the derivative of the <strong>inside</strong> function.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Common Forms</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">General Power Rule</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx} [ (g(x))^n ] = n(g(x))^{n-1} \cdot g'(x)$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Trigonometric</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx} [ \sin(g(x)) ] = \cos(g(x)) \cdot g'(x)$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Exponential</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx} [ e^{g(x)} ] = e^{g(x)} \cdot g'(x)$$" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">5. Repeated Use (Chain Rule Inception)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sometimes functions are nested three layers deep.
            </p>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-900">Example: <LatexText text="$y = \sin^4(5x) = (\sin(5x))^4$" /></h4>
              <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm mt-2">
                <li>Outer layer: Power rule <LatexText text="$(\dots)^4$" /></li>
                <li>Middle layer: Sine function <LatexText text="$\sin(\dots)$" /></li>
                <li>Inner layer: <LatexText text="$5x$" /></li>
              </ul>
              <div className="mt-3 bg-white p-3 rounded border border-purple-200">
                <LatexText text="$$y' = 4(\sin(5x))^3 \cdot \cos(5x) \cdot 5 = 20\sin^3(5x)\cos(5x)$$" />
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> A common error is differentiating the "inside" function too early. In the first step (<LatexText text="$f'(g(x))$" />), the <LatexText text="$g(x)$" /> must remain unchanged. You only differentiate <LatexText text="$g(x)$" /> in the second step (the multiplication).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Implicit Differentiation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Explicit vs. Implicit</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Explicit Form</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$y$" /> is isolated on one side (e.g., <LatexText text="$y = x^2 - 5x$" />).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Implicit Form</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$x$" /> and <LatexText text="$y$" /> are mixed together (e.g., <LatexText text="$x^2 + y^2 = 25$" /> or <LatexText text="$\sin(xy) = x$" />).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. The Procedure</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you cannot solve for <LatexText text="$y$" /> easily, you differentiate the equation term by term with respect to <LatexText text="$x$" />.
            </p>
            <div className="space-y-2 text-slate-700 mb-4">
              <p><strong>1.</strong> Differentiate both sides of the equation with respect to <LatexText text="$x$" />.</p>
              <p><strong>2.</strong> <strong>Crucial Step:</strong> Whenever you differentiate a term containing <LatexText text="$y$" />, you must apply the Chain Rule. Multiply that term by <LatexText text="$\frac{dy}{dx}$" /> (or <LatexText text="$y'$" />).</p>
              <p className="text-sm text-slate-600 italic">Why? Because <LatexText text="$y$" /> is a function of <LatexText text="$x$" />. <LatexText text="$\frac{d}{dx}(y^3) = 3y^2 \cdot \frac{dy}{dx}$" />.</p>
              <p><strong>3.</strong> Group all terms containing <LatexText text="$\frac{dy}{dx}$" /> on one side of the equation.</p>
              <p><strong>4.</strong> Factor out <LatexText text="$\frac{dy}{dx}$" /> and solve for it.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Product and Quotient Rules in Implicit Contexts</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Implicit problems frequently mix <LatexText text="$x$" /> and <LatexText text="$y$" /> in products. You must use the Product Rule.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">Example: Differentiate <LatexText text="$xy = 10$" /></h4>
              <div className="mt-3 bg-white p-3 rounded border border-green-200">
                <LatexText text="$$\frac{d}{dx}(xy) = (1)(y) + (x)(1)\frac{dy}{dx} = y + x\frac{dy}{dx}$$" />
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> When finding a numerical slope at a point using implicit differentiation, it is usually algebraically easier to substitute the numerical values for <LatexText text="$x$" /> and <LatexText text="$y$" /> <em>immediately after differentiation</em>, rather than solving for the algebra expression <LatexText text="$\frac{dy}{dx}$" /> first.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Differentiating Inverse Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$f$" /> and <LatexText text="$g$" /> are inverse functions (meaning <LatexText text="$f(g(x)) = x$" />), their slopes are related. The graphs of inverse functions are reflections across the line <LatexText text="$y=x$" />. Therefore, the tangent lines at corresponding points are also reflections.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. The Relationship</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$(a, b)$" /> is a point on the graph of <LatexText text="$f$" />, then <LatexText text="$(b, a)$" /> is a point on the graph of <LatexText text="$f^{-1}$" />. The slope of the inverse function at <LatexText text="$x=b$" /> is the <strong>reciprocal</strong> of the slope of the original function at <LatexText text="$x=a$" />.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">The Formula</h4>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$(f^{-1})'(b) = \frac{1}{f'(f^{-1}(b))}$$" />
              </div>
              <p className="text-sm text-slate-700 mt-3">Or more simply:</p>
              <div className="mt-2 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$(f^{-1})'(b) = \frac{1}{f'(a)}$$" />
              </div>
              <p className="text-sm text-slate-700 mt-2">Where <LatexText text="$f(a) = b$" />.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Procedure for AP Problems</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">The problem asks for <LatexText text="$(f^{-1})'(x)$" /> at a specific value, say <LatexText text="$x=5$" /></h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Set the <em>original</em> function <LatexText text="$f(x) = 5$" /></h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Solve for <LatexText text="$x$" /> (let's say <LatexText text="$x=2$" />). This means <LatexText text="$f(2)=5$" />, so the inverse point is <LatexText text="$(5,2)$" /></h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find <LatexText text="$f'(x)$" /></h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Plug <LatexText text="$x=2$" /> into <LatexText text="$f'(x)$" /> to get <LatexText text="$f'(2)$" /></h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">6</span>
                <div>
                  <h4 className="font-semibold text-green-800">Take the reciprocal. The answer is <LatexText text="$\frac{1}{f'(2)}$" /></h4>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Differentiating Inverse Trigonometric Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Essential Formulas</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You must memorize the derivatives of the inverse trig functions. Note that the "co" functions (cosine, cotangent, cosecant) are simply the negative versions of their counterparts.
            </p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Arcsine</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}(\sin^{-1} u) = \frac{1}{\sqrt{1-u^2}} \cdot u'$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Arccosine</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}(\cos^{-1} u) = -\frac{1}{\sqrt{1-u^2}} \cdot u'$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Arctangent</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}(\tan^{-1} u) = \frac{1}{1+u^2} \cdot u'$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Arccotangent</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}(\cot^{-1} u) = -\frac{1}{1+u^2} \cdot u'$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Arcsecant</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}(\sec^{-1} u) = \frac{1}{|u|\sqrt{u^2-1}} \cdot u'$$" />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Arccosecant</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}(\csc^{-1} u) = -\frac{1}{|u|\sqrt{u^2-1}} \cdot u'$$" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Common Domain Restrictions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Recall that for these derivatives to exist, the function must be defined. For <LatexText text="$\arcsin x$" /> and <LatexText text="$\arccos x$" />, the domain is <LatexText text="$[-1, 1]$" />, and they are differentiable on <LatexText text="$(-1, 1)$" />. For <LatexText text="$\arctan x$" />, the domain is all real numbers.
            </p>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> Arcsine and Arctangent are the most frequently tested inverse trig derivatives. Ensure you apply the Chain Rule (<LatexText text="$u'$" />) to the numerator.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Selecting Procedures for Calculating Derivatives",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Strategy and Recognition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This topic is about metacognition: looking at a function and deciding <em>which</em> rule applies before starting.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Power Rule vs. Exponential Rule</h4>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm mt-2">
                  <li><LatexText text="$x^5$" />: Variable base, constant power <LatexText text="$\rightarrow$" /> Power Rule (<LatexText text="$5x^4$" />)</li>
                  <li><LatexText text="$5^x$" />: Constant base, variable power <LatexText text="$\rightarrow$" /> Exponential Rule (<LatexText text="$5^x \ln 5$" />)</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Function Raised to a Function</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$x^x$" />: Variable base, variable power <LatexText text="$\rightarrow$" /> <strong>Logarithmic Differentiation</strong></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Logarithmic Differentiation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Used when a function is complex (lots of products/quotients/powers) or has the form <LatexText text="$y = f(x)^{g(x)}$" />.
            </p>
            <div className="space-y-2 text-slate-700 mb-4">
              <p><strong>Steps:</strong></p>
              <p><strong>1.</strong> Take the natural log (<LatexText text="$\ln$" />) of both sides: <LatexText text="$\ln y = \ln(\dots)$" />.</p>
              <p><strong>2.</strong> Use log properties to expand the right side (bring exponents down, split products into sums).</p>
              <p><strong>3.</strong> Differentiate implicitly with respect to <LatexText text="$x$" />: <LatexText text="$\frac{1}{y} \frac{dy}{dx} = \dots$" /></p>
              <p><strong>4.</strong> Multiply by <LatexText text="$y$" /> (substituting the original equation back in for <LatexText text="$y$" />) to isolate <LatexText text="$\frac{dy}{dx}$" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Calculating Higher-Order Derivatives",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Notation and Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Higher-order derivatives are simply derivatives of derivatives.
            </p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">First Derivative</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$y'$" />, <LatexText text="$f'(x)$" />, <LatexText text="$\frac{dy}{dx}$" /> (Velocity)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Second Derivative</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$y''$" />, <LatexText text="$f''(x)$" />, <LatexText text="$\frac{d^2y}{dx^2}$" /> (Acceleration)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Third Derivative</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$y'''$" />, <LatexText text="$f'''(x)$" />, <LatexText text="$\frac{d^3y}{dx^3}$" /> (Jerk)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">nth Derivative</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$y^{(n)}$" />, <LatexText text="$f^{(n)}(x)$" />, <LatexText text="$\frac{d^ny}{dx^n}$" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Implicit Higher-Order Derivatives</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This is a high-frequency FRQ topic.
            </p>
            <div className="space-y-2 text-slate-700 mb-4">
              <p><strong>1.</strong> Find <LatexText text="$\frac{dy}{dx}$" /> implicitly.</p>
              <p><strong>2.</strong> Differentiate <LatexText text="$\frac{dy}{dx}$" /> again to find <LatexText text="$\frac{d^2y}{dx^2}$" />. This will likely require the Quotient Rule.</p>
              <p><strong>3.</strong> <strong>Crucial Step:</strong> The expression for <LatexText text="$\frac{d^2y}{dx^2}$" /> will likely contain <LatexText text="$y$" /> and <LatexText text="$\frac{dy}{dx}$" />. You must substitute the expression you found in Step 1 for <LatexText text="$\frac{dy}{dx}$" /> into the second derivative to get the final answer in terms of <LatexText text="$x$" /> and <LatexText text="$y$" /> only.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-summary",
      title: "Summary of Key Skills for Unit 3",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Essential Competencies</h3>
            <ul className="list-disc pl-6 space-y-3 text-slate-700">
              <li><strong>Decomposition:</strong> Ability to break a composite function <LatexText text="$f(g(x))$" /> into its components to apply the Chain Rule.</li>
              <li><strong>Notational Fluency:</strong> Understanding that <LatexText text="$\frac{d}{dx}f(g(x))$" /> is distinct from <LatexText text="$\frac{d}{dx}f(x) \cdot \frac{d}{dx}g(x)$" /> (the latter is incorrect).</li>
              <li><strong>Implicit Manipulation:</strong> Algebraically solving for <LatexText text="$\frac{dy}{dx}$" /> after differentiating a messy equation.</li>
              <li><strong>Inverse Logic:</strong> Switching <LatexText text="$x$" /> and <LatexText text="$y$" /> mentally to utilize the inverse derivative formula <LatexText text="$\frac{1}{f'(f^{-1}(x))}$" />.</li>
              <li><strong>Higher-Order Derivatives:</strong> Finding second and third derivatives, especially in implicit contexts where substitution is required.</li>
            </ul>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit3Notes;